import { Stack, Box, Divider } from '@mui/material'
import { Card, Skeleton } from '@repo/ui'
import { StickyFormHeader } from 'components/navigation/StickyFormHeader/StickyFormHeader'
import { vhMinus } from 'utils/textZoom'

/**
 * Loading placeholder for the Studio page (§19 — skeletons fill the SAME space
 * the loaded content will occupy, so nothing shifts in on load). This mirrors
 * the real StudioPage footprint: the pinned chrome block (title + Cancel/Save +
 * the Certificate/Email tab row + preview controls) and the editor↔preview split
 * (left cards: Logo / Colors / Fonts / Buttons; right: the tall device-framed
 * preview pane).
 *
 * The bottom save bar it used to mirror is gone (VRG-221) — those buttons are in
 * the header now, and the pane grew into the 76px the bar occupied. Both halves of
 * that move have to land here too or the skeleton stops standing for the page.
 *
 * It uses the SAME `StickyFormHeader` as the page rather than restating its
 * geometry, so the block's height — the number that decides where everything below
 * it starts — matches by construction instead of by two sets of numbers agreeing.
 */
export const StudioSkeleton = () => {
  // One field row: label (2 lines) + the input — matches a ThemeForm field.
  const FieldRow = ({ input = 44 }: { input?: number }) => (
    <Stack spacing={2}>
      <Skeleton lines={2} sx={{ maxWidth: 220 }} />
      <Skeleton variant="rounded" height={input} />
    </Stack>
  )

  return (
    <>
      {/* Header actions. Widths are the rendered buttons', measured at 1280x800: Cancel
          81.9px, Save 66.7px, both 40 tall. `Header` supplies the 12px gap. */}
      <StickyFormHeader
        title="Studio"
        actions={
          <>
            <Skeleton variant="rounded" width={82} height={40} />
            <Skeleton variant="rounded" width={67} height={40} />
          </>
        }
      >
        <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Preview controls — right-aligned with the tabs (flow + device selects).
            40px tall, not 36: the rendered MenuButtons measure 40 (137 and 111 wide, 8px
            apart, hard against the 1256 content edge). Widths are a loading state's guess
            since they size to labels it cannot know yet, but they only move the left edge —
            the stack is right-anchored, so the footprint that matters is the height. */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ position: 'absolute', right: 0, top: 6, height: 40 }}
        >
          <Skeleton variant="rounded" width={137} height={40} />
          <Skeleton variant="rounded" width={111} height={40} />
        </Stack>

        {/* Tab row (Certificate / Email).
            Every number here is the loaded row's, measured at 1280x800 — this block sets where
            EVERYTHING below it starts, so being short here moves the whole split up. It was:
            two 18px bars with `pb: 1.5`, i.e. 24px against the real row's 52, which put the
            preview pane's top at 133.5 against the loaded page's 161.5.
              row     52px tall (MuiTabs flexContainer) + the 1px Divider = 53
              labels  19px line box, centred -> top 109 against the real text's 108.5
              x       text starts 16px in (the Tab's own padding) -> `pl: 4`; "Certificate" is
                      74.9 wide and "Email" 38.8, with 31.9px between them -> `spacing={8}` */}
        <Stack direction="row" spacing={8} alignItems="center" sx={{ height: 52, pl: 4 }}>
          <Skeleton width={75} height={19} />
          <Skeleton width={39} height={19} />
        </Stack>
        <Divider />
        </Box>
      </StickyFormHeader>

      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Editor ↔ preview split (default ~58 / 42, matching ResizableSplit).
            No `pt` — StickyFormHeader's own `pb: 4` supplies the 16px, exactly as on the page.
            The gap is ResizableSplit's drag handle, which is 18px — at `gap: 3` (12px) the
            preview pane started at 858.1 against the loaded page's 864.1. */}
        <Box sx={{ display: 'flex', gap: '18px' }}>
          {/* Left — editor cards */}
          <Box sx={{ flex: '0 0 58%', minWidth: 0 }}>
            <Stack spacing={4}>
              {/* Logo card: title + mark/wordmark tiles + alignment + size */}
              <Card spacing={6}>
                <Skeleton width={60} height={20} />
                <Stack spacing={4}>
                  <Skeleton lines={1} sx={{ maxWidth: 260 }} />
                  <Stack direction="row" spacing={3}>
                    <Skeleton variant="rounded" height={96} sx={{ flex: 1 }} />
                    <Skeleton variant="rounded" height={96} sx={{ flex: 1 }} />
                  </Stack>
                </Stack>
                <Divider />
                <FieldRow input={40} />
                <Divider />
                <FieldRow input={40} />
              </Card>

              {/* Colors card: title + 4 color fields */}
              <Card spacing={6}>
                <Skeleton width={70} height={20} />
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i}>
                    <FieldRow />
                    {i < 3 && <Divider sx={{ mt: 4 }} />}
                  </Box>
                ))}
              </Card>

              {/* Fonts card: title + 2 font selects */}
              <Card spacing={6}>
                <Skeleton width={56} height={20} />
                <FieldRow />
                <Divider />
                <FieldRow />
              </Card>

              {/* Buttons card: title + radius slider */}
              <Card spacing={6}>
                <Skeleton width={84} height={20} />
                <Stack spacing={3}>
                  <Skeleton lines={2} sx={{ maxWidth: 220 }} />
                  <Skeleton variant="rounded" height={12} />
                </Stack>
              </Card>
            </Stack>
          </Box>

          {/* Right — preview pane (same height/footprint as the real Preview) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={(theme) => ({
                border: `1px solid ${theme.palette.grey[100]}`,
                borderRadius: 3,
                backgroundColor: theme.palette.grey[50],
                height: vhMinus(185),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                position: 'sticky',
                // 138.5, matching Preview's STUDIO_PANE_STICKY_TOP — the bottom of the pinned
                // chrome block above (border included), which is also this pane's resting top.
                top: 138.5,
              })}
            >
              {/* Centered device frame */}
              <Skeleton variant="rounded" width={260} height={520} />
              <Skeleton width={96} height={14} />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
